import { useMsal } from '@azure/msal-react'
import {
  faBolt,
  faCircleInfo,
  faDollarSign,
  faEllipsis,
  faEye,
  faEyeSlash,
  faHashtag,
  faImages,
  faLocationDot,
  faPencil,
  faTerminal,
  faTrash,
  faTv,
  faUpload,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  AspectRatio,
  Badge,
  Button,
  Group,
  Image,
  LoadingOverlay,
  Menu,
  Modal,
  Paper,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSessionInfo } from '../../contexts/SessionContext'
import { deleteDevice, updateDeviceVisibility } from '../../data/device.service'
import { Device } from '../../models/Device'
import { DeviceAdvertisements } from '../../models/DeviceAdvertisements'
import { Location } from '../../models/Location'
import {
  AdStatuses,
  COLORS,
  HostOnboardingSteps,
  defaultImageUrl,
} from '../../utils/constants'
import { formatNumber } from '../../utils/displayUtils'
import classes from '../css/DeviceCard.module.css'
import RecoveryCodeModal from './RecoveryCodeModal'
import { UploadAdModal } from './UploadAdModal'
import { isUserAdmin } from '../../utils/authUtils'

const DeviceCard = ({
  device: initialDevice,
  location,
  organizationId,
  onBookingClick,
  onDeviceDeleted,
  isIncomplete,
}: DeviceCardProps) => {
  const { session } = useSessionInfo()
  const navigate = useNavigate()
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [recoveryCodeModalOpen, setRecoveryCodeModalOpen] = useState(false)
  const [uploadAdModalOpen, setUploadAdModalOpen] = useState(false)
  const { instance } = useMsal()
  const [device, setDevice] = useState(initialDevice)
  const [isVisible, setIsVisible] = useState(device.isVisible)
  const [isDeleting, setIsDeleting] = useState(false)

  // Check if advertiser's categories are restricted for this device
  const isAdvertiserRestricted = () => {
    if (!session?.organizationId || !device.restrictedCategoryIds?.length)
      return false

    // If the current organization's categories intersect with the device's restricted categories
    return (
      session.organizationCategories?.some((category) =>
        device.restrictedCategoryIds?.includes(category.id),
      ) ?? false
    )
  }

  // Get the restricted categories that match the advertiser's categories
  const getRestrictedCategories = () => {
    if (!session?.organizationCategories || !device.restrictedCategoryIds)
      return []

    return session.organizationCategories
      .filter((category) => device.restrictedCategoryIds?.includes(category.id))
      .map((category) => category.name)
  }

  const getFirstRestrictedCategory = () => {
    const categories = getRestrictedCategories()
    return categories.length > 0 ? categories[0] : ''
  }

  const handleAdUploaded = (newAdvertisement: DeviceAdvertisements) => {
    setDevice((prevDevice) => ({
      ...prevDevice,
      deviceAdvertisements: [
        ...prevDevice.deviceAdvertisements,
        newAdvertisement,
      ],
    }))
  }

  function enableDevice(): void {
    const updatedDevice = { ...device, isVisible: true }
    updateDeviceVisibility(updatedDevice, instance).then(() => {
      setIsVisible(true)
    })
  }

  function disableDevice(): void {
    const updatedDevice = { ...device, isVisible: false }
    updateDeviceVisibility(updatedDevice, instance).then(() => {
      setIsVisible(false)
    })
  }

  const handleDeleteDevice = async () => {
    try {
      setIsDeleting(true)
      await deleteDevice(device, instance)

      notifications.show({
        title: 'Device Deleted',
        message: 'The device has been successfully deleted.',
        color: 'green',
      })

      if (onDeviceDeleted) {
        onDeviceDeleted()
      }
      navigate(`/locations/${device.locationId}`)
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete the device.',
        color: 'red',
      })
    } finally {
      setIsDeleting(false)
      setDeleteModalOpen(false)
    }
  }

  return (
    <>
      <Modal
        opened={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title='Delete Device'
        centered
        size='sm'
        radius='lg'
      >
        <Stack pos='relative'>
          {isDeleting && <LoadingOverlay />}
          <Text>
            Are you sure you want to delete this device? This action cannot be
            undone.
          </Text>
          <Group justify='flex-end' mt='md'>
            <Button
              variant='light'
              onClick={() => setDeleteModalOpen(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              color='red'
              onClick={handleDeleteDevice}
              loading={isDeleting}
              disabled={isDeleting}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>

      <RecoveryCodeModal
        opened={recoveryCodeModalOpen}
        onClose={() => setRecoveryCodeModalOpen(false)}
        device={device}
      />

      {uploadAdModalOpen && (
        <UploadAdModal
          opened={uploadAdModalOpen}
          onClose={() => setUploadAdModalOpen(false)}
          deviceId={device.id!}
          deviceName={device.name}
          organizationId={session?.organizationId!}
          onAdUploaded={handleAdUploaded}
        />
      )}

      <Paper shadow='md' radius='lg' p='lg' withBorder>
        <div style={{ position: 'relative' }}>
          <AspectRatio ratio={4 / 3}>
            <Image
              src={device.imageUrl || defaultImageUrl}
              alt={device.name || 'Device'}
              className={classes.image}
            />
          </AspectRatio>
          {device.approvalType === 'Auto' && (
            <Tooltip label='Advertisements will be automatically approved'>
              <Badge
                color='#228be6'
                variant='filled'
                leftSection={<FontAwesomeIcon icon={faBolt} />}
                style={{
                  position: 'absolute',
                  top: 12,
                  right: 12,
                  backgroundColor: COLORS.HiddenGemzGold,
                }}
              >
                Auto Approval
              </Badge>
            </Tooltip>
          )}
        </div>
        <Stack gap={8}>
          <Stack ml={20} mr={20} mt={15} mb={10}>
            <Group justify='space-between' w='100%'>
              {session?.organizationId === organizationId ||
              isUserAdmin(session?.user?.id) ? (
                isIncomplete ? (
                  <Title size='h3'>{device.name || 'Unnamed Device'}</Title>
                ) : (
                  <Link
                    to={`/devices/${device.id}`}
                    style={{
                      color: COLORS.HiddenGemzRed,
                      textDecoration: 'none',
                      transition: 'all 0.2s ease',
                    }}
                    className={classes.deviceNameLink}
                  >
                    <Title size='h3'>{device.name || 'Unnamed Device'}</Title>
                  </Link>
                )
              ) : (
                <Title size='h3'>{device.name || 'Unnamed Device'}</Title>
              )}
              <Group gap='sm'>
                {isIncomplete && (
                  <Badge color={COLORS.HiddenGemzRed} size='sm'>
                    Incomplete
                  </Badge>
                )}
                {session?.organizationId === organizationId && (
                  <>
                    {!isIncomplete && (
                      <>
                        {isVisible ? (
                          <Tooltip label='Others can see or book this device'>
                            <Badge
                              color='#136139'
                              variant='filled'
                              leftSection={
                                <FontAwesomeIcon icon={faEye} color='white' />
                              }
                            >
                              Active
                            </Badge>
                          </Tooltip>
                        ) : (
                          <Tooltip label='Others can not see or book this device'>
                            <Badge
                              color='#ff6363'
                              variant='filled'
                              leftSection={
                                <FontAwesomeIcon
                                  icon={faEyeSlash}
                                  color='white'
                                />
                              }
                            >
                              Inactive
                            </Badge>
                          </Tooltip>
                        )}
                      </>
                    )}

                    <Menu shadow='md' width={200}>
                      <Menu.Target>
                        <ActionIcon
                          bg={COLORS.LightGray}
                          style={{
                            borderRadius: 12,
                            padding: 10,
                            width: 40,
                            height: 40,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faEllipsis}
                            size='lg'
                            color='black'
                          />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<FontAwesomeIcon icon={faPencil} />}
                          onClick={() =>
                            navigate(`/devices/edit/${device.id}`, {
                              state: {
                                location,
                                device: device,
                                isHostOnboarding: false,
                                currentStep: HostOnboardingSteps.DevicePhotos,
                              },
                            })
                          }
                        >
                          Edit Device
                        </Menu.Item>
                        {!isIncomplete && (
                          <>
                            <Menu.Item
                              leftSection={
                                <FontAwesomeIcon icon={faTerminal} />
                              }
                              onClick={() => setRecoveryCodeModalOpen(true)}
                            >
                              Get Recovery Code
                            </Menu.Item>

                            <Menu.Item
                              leftSection={<FontAwesomeIcon icon={faImages} />}
                              onClick={() => navigate(`/devices/${device.id}`)}
                            >
                              View Running Ads
                            </Menu.Item>

                            {isVisible ? (
                              <Menu.Item
                                leftSection={
                                  <FontAwesomeIcon icon={faEyeSlash} />
                                }
                                onClick={() => {
                                  try {
                                    disableDevice()
                                    notifications.show({
                                      title: 'Device Hidden',
                                      message:
                                        'The device has been successfully hidden.',
                                      color: 'green',
                                    })
                                  } catch (error: any) {
                                    notifications.show({
                                      title: 'Error',
                                      message:
                                        'Cannot change device visibility.',
                                      color: 'red',
                                    })
                                  }
                                }}
                                rightSection={
                                  <Tooltip label='Disallow others to book ads on this device'>
                                    <span>
                                      <FontAwesomeIcon icon={faCircleInfo} />
                                    </span>
                                  </Tooltip>
                                }
                              >
                                Hide Device
                              </Menu.Item>
                            ) : (
                              <Menu.Item
                                leftSection={<FontAwesomeIcon icon={faEye} />}
                                onClick={() => {
                                  try {
                                    enableDevice()
                                    notifications.show({
                                      title: 'Device Shown',
                                      message:
                                        'The device has been successfully made visible.',
                                      color: 'green',
                                    })
                                  } catch (error: any) {
                                    notifications.show({
                                      title: 'Error',
                                      message:
                                        'Cannot change device visibility.',
                                      color: 'red',
                                    })
                                  }
                                }}
                                rightSection={
                                  <Tooltip label='Allow others to book ads on this device'>
                                    <span>
                                      <FontAwesomeIcon icon={faCircleInfo} />
                                    </span>
                                  </Tooltip>
                                }
                              >
                                Show Device
                              </Menu.Item>
                            )}

                            <Menu.Item
                              leftSection={<FontAwesomeIcon icon={faTrash} />}
                              color='red'
                              onClick={() => setDeleteModalOpen(true)}
                            >
                              Delete Device
                            </Menu.Item>
                          </>
                        )}
                      </Menu.Dropdown>
                    </Menu>
                  </>
                )}
              </Group>
            </Group>
            <Group justify='space-between'>
              <Group gap={5} flex={1}>
                <FontAwesomeIcon icon={faTv} size='xs' width={15} />
                <Text className={classes.labelText}>Display</Text>
              </Group>
              <Text className={classes.detailText} flex={1}>
                {device.screenSize
                  ? `${device.screenSize}" TV`
                  : 'Not Configured'}
              </Text>
            </Group>
            <Group justify='space-between'>
              <Group gap={5} flex={1}>
                <FontAwesomeIcon icon={faLocationDot} size='xs' width={15} />
                <Text className={classes.labelText} flex={1}>
                  Display Location
                </Text>
              </Group>

              <Text className={classes.detailText} flex={1}>
                {device.name || 'Not Configured'}
              </Text>
            </Group>
            <Group justify='space-between'>
              <Group gap={5} flex={1}>
                <FontAwesomeIcon icon={faDollarSign} size='xs' width={15} />
                <Text className={classes.labelText} flex={1}>
                  Monthly Price
                </Text>
              </Group>

              <Text className={classes.detailText} flex={1}>
                {device.monthlyPrice
                  ? `$${formatNumber(device.monthlyPrice)}/month`
                  : 'Not Configured'}
              </Text>
            </Group>
            <Group justify='space-between'>
              <Group gap={5} flex={1}>
                <FontAwesomeIcon icon={faHashtag} size='xs' width={15} />
                <Text className={classes.labelText} flex={1}>
                  Running Ads
                </Text>
              </Group>

              <Text className={classes.detailText} flex={1}>
                {formatNumber(
                  device.deviceAdvertisements.filter(
                    (ad) => ad.status === AdStatuses.Running,
                  ).length,
                )}{' '}
                Ads
              </Text>
            </Group>
          </Stack>
          {(session?.organizationId === organizationId ||
            isUserAdmin(session?.user.id)) &&
          !isIncomplete ? (
            <Button
              mt={10}
              radius='md'
              leftSection={<FontAwesomeIcon icon={faUpload} />}
              onClick={() => setUploadAdModalOpen(true)}
            >
              Upload Your Own Ad
            </Button>
          ) : (
            !isIncomplete && (
              <Stack gap={8}>
                <Button
                  mt={10}
                  radius='md'
                  onClick={onBookingClick}
                  disabled={isAdvertiserRestricted()}
                >
                  {session != null ? 'Book Now!' : 'Log in to Book'}
                </Button>
                {isAdvertiserRestricted() && (
                  <Text
                    size='sm'
                    c='dimmed'
                    ta='center'
                    style={{ fontStyle: 'italic' }}
                  >
                    This device is configured to block ads from your
                    organization's category: {getFirstRestrictedCategory()}
                  </Text>
                )}
              </Stack>
            )
          )}
        </Stack>
      </Paper>
    </>
  )
}

export default DeviceCard

interface DeviceCardProps {
  location: Location
  device: Device
  organizationId: number
  onBookingClick: () => void
  onDeviceDeleted?: () => void
  isIncomplete?: boolean
}
